<template>
    <div class="swiper-container" ref="swiper">
        <div class="swiper-wrapper">
            <slot name="slides"></slot>
        </div>
        <div class="swiper-toolbar" v-if="displayToolbar">
            <div class="swiper-pagination" ref="pagination"></div>
            <template v-if="!$breakpoint.isS">
                <div class="swiper-button-prev" ref="prevEl"></div>
                <div class="swiper-button-next" ref="nextEl"></div>
            </template>
        </div>
    </div>
</template>

<script>
import Swiper, { Navigation, Pagination } from 'swiper'

Swiper.use([Navigation, Pagination])

export default {
    name: 'SwiperWrapper',
    props: {
        options: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            swiper: null
        }
    },
    computed: {
        displayToolbar() {
            return this.options.navigation || this.options.pagination
        }
    },
    mounted() {
        this.$nextTick(this.initSwiper)
    },
    beforeDestroy() {
        this.swiper.destroy()
    },
    methods: {
        initSwiper() {
            const { swiper, pagination, nextEl, prevEl } = this.$refs
            const options = {
                speed: 350,
                ...this.options,
                ...(this.options.navigation && {
                    navigation: { nextEl, prevEl }
                }),
                ...(this.options.pagination && {
                    pagination: {
                        el: pagination,
                        ...this.options.pagination
                    }
                }),
                on: {
                    init: () => {
                        this.$emit('init')
                    }
                }
            }
            this.swiper = new Swiper(swiper, options)
        }
    }
}
</script>

<style lang="sass" scoped>
@import nespresso-bi/utilities
@import nespresso-bi/brand/_variables
.swiper-container
    --swiper-navigation-size: 0.5em
    width: 100%
    box-sizing: border-box

.swiper-slide
    height: auto
    flex-grow: 1

.swiper-pagination
    bottom: ($gap * 3.5)
    width: 100%

.swiper-button-disabled
    opacity: 0.125

.swiper-button-prev,
.swiper-button-next
    background-color: $color_dark
    border-radius: 50%
    box-shadow: 0 1px 2px rgba(0,0,0,0.07), 0 2px 4px rgba(0,0,0,0.07), 0 4px 8px rgba(0,0,0,0.07), 0 8px 16px rgba(0,0,0,0.07)
    height: ($gap * 2)
    width: ($gap * 2)
    color: $color_white
    transition: opacity 150ms ease

.swiper-button-prev
    left: ($gap * 2.5)

.swiper-button-next
    right: ($gap * 2.5)

::v-deep
    .swiper-pagination-bullets
        display: flex
        justify-content: center

    .swiper-pagination-bullet
        position: relative
        height: pxToRem(8)
        width: pxToRem(8)
        background-color: transparent
        border: 1px solid
        border-color: $color-white
        border-radius: 50%
        opacity: 1

        &::before
            +pos(absolute, -1px, -1px, -1px, -1px)
            content: ''
            background-color: $color-white
            border-radius: inherit
            transform: scale(0)
            opacity: 0
            transition: opacity 150ms ease, transform 150ms ease

        &-active
            &::before
                opacity: 1
                transform: scale(1)

        + .swiper-pagination-bullet
            margin-left: 1em

+desktop
    .swiper-container
        max-width: 62.25em

    .swiper-pagination
        bottom: ($gap * 1.5)
        width: 100%

    ::v-deep
        .swiper-pagination-bullet
            height: pxToRem(10)
            width: pxToRem(10)
            transition: background-color $easing $duration_fast

            &:hover
                background-color: transparentize($color_white, 0.35)


// Legacy styles for IE11 compatibility
@media all and (-ms-high-contrast:none)
    .swiper-button-prev,
    .swiper-button-next
      position: absolute
      top: 50%
      width: 27px
      height: 44px
      margin-top: -22px
      z-index: 10
      cursor: pointer
      background-size: 27px 44px
      background-position: center
      background-repeat: no-repeat

    .swiper-button-prev.swiper-button-disabled,
    .swiper-button-next.swiper-button-disabled
      opacity: 0.35
      cursor: auto
      pointer-events: none

    .swiper-button-prev,
    .swiper-container-rtl .swiper-button-next
      background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D'http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg'%20viewBox%3D'0%200%2027%2044'%3E%3Cpath%20d%3D'M0%2C22L22%2C0l2.1%2C2.1L4.2%2C22l19.9%2C19.9L22%2C44L0%2C22L0%2C22L0%2C22z'%20fill%3D'%23ffffff'%2F%3E%3C%2Fsvg%3E")
      left: 10px
      right: auto

    .swiper-button-next,
    .swiper-container-rtl .swiper-button-prev
      background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D'http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg'%20viewBox%3D'0%200%2027%2044'%3E%3Cpath%20d%3D'M27%2C22L27%2C22L5%2C44l-2.1-2.1L22.8%2C22L2.9%2C2.1L5%2C0L27%2C22L27%2C22z'%20fill%3D'%23ffffff'%2F%3E%3C%2Fsvg%3E")
      right: 10px
      left: auto

    .swiper-button-prev.swiper-button-white,
    .swiper-container-rtl .swiper-button-next.swiper-button-white
      background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D'http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg'%20viewBox%3D'0%200%2027%2044'%3E%3Cpath%20d%3D'M0%2C22L22%2C0l2.1%2C2.1L4.2%2C22l19.9%2C19.9L22%2C44L0%2C22L0%2C22L0%2C22z'%20fill%3D'%23ffffff'%2F%3E%3C%2Fsvg%3E")

    .swiper-button-next.swiper-button-white,
    .swiper-container-rtl .swiper-button-prev.swiper-button-white
      background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D'http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg'%20viewBox%3D'0%200%2027%2044'%3E%3Cpath%20d%3D'M27%2C22L27%2C22L5%2C44l-2.1-2.1L22.8%2C22L2.9%2C2.1L5%2C0L27%2C22L27%2C22z'%20fill%3D'%23ffffff'%2F%3E%3C%2Fsvg%3E")

    .swiper-button-prev.swiper-button-black,
    .swiper-container-rtl .swiper-button-next.swiper-button-black
      background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D'http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg'%20viewBox%3D'0%200%2027%2044'%3E%3Cpath%20d%3D'M0%2C22L22%2C0l2.1%2C2.1L4.2%2C22l19.9%2C19.9L22%2C44L0%2C22L0%2C22L0%2C22z'%20fill%3D'%23000000'%2F%3E%3C%2Fsvg%3E")

    .swiper-button-next.swiper-button-black,
    .swiper-container-rtl .swiper-button-prev.swiper-button-black
      background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D'http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg'%20viewBox%3D'0%200%2027%2044'%3E%3Cpath%20d%3D'M27%2C22L27%2C22L5%2C44l-2.1-2.1L22.8%2C22L2.9%2C2.1L5%2C0L27%2C22L27%2C22z'%20fill%3D'%23000000'%2F%3E%3C%2Fsvg%3E")

    .swiper-button-lock
      display: none
</style>
